import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { UserPicker } from "@/components/users/UserPicker";
import { useCreateWorkTask, useTaskTags } from "../hooks/useWorkTasks";
import type {
  CreateWorkTaskDto,
  WorkTaskPriority,
  WorkTaskRelatedEntityType,
} from "@/api/workTasks";
import { ApiError } from "@/api/client";
import { AttachResourceModal } from "@/components/messaging/AttachResourceModal";
import { taskTypeForMessageType } from "@/types/recordTypeBridge";
import { calendarDateInputToIso } from "@/utils/datetime";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set, the modal pre-fills the related-entity fields and locks them. */
  prefilledRelatedType?: WorkTaskRelatedEntityType;
  prefilledRelatedEntityId?: string;
  prefilledRelatedEntityLabel?: string;
  /** When true, navigate to the new task on save; default false (toast + close). */
  navigateOnSave?: boolean;
  onCreated?: (taskId: string) => void;
}

const PRIORITIES: WorkTaskPriority[] = ["Low", "Medium", "High", "Critical"];

export function QuickAddTaskModal({
  open,
  onClose,
  prefilledRelatedType,
  prefilledRelatedEntityId,
  prefilledRelatedEntityLabel,
  navigateOnSave = false,
  onCreated,
}: Props) {
  const { user, hasPermission } = useAuth();
  const canAssignToOthers = hasPermission("Tasks.AssignToOthers");
  const tagsQ = useTaskTags();
  const create = useCreateWorkTask();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<WorkTaskPriority>("Medium");
  const [dueDate, setDueDate] = useState("");
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [titleError, setTitleError] = useState<string | null>(null);
  // Tasks Module v2 — multiuser (shared) task. Open to all creators (no gate).
  const [isMultiuser, setIsMultiuser] = useState(false);
  const [participants, setParticipants] = useState<{ id: string; displayName: string }[]>([]);
  // Bumped after each add so the add-picker remounts empty.
  const [participantPickerKey, setParticipantPickerKey] = useState(0);
  // Attach-record expansion (WS5) — when the modal is opened
  // free-standing (no `prefilledRelatedType`), the user can pick any
  // registered entity via the shared `AttachResourceModal`. The
  // picker yields a message-shaped attachment which we translate
  // through `taskTypeForMessageType` into the task's PascalCase enum.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedAttachment, setPickedAttachment] = useState<{
    type: WorkTaskRelatedEntityType;
    id: string;
    label: string;
  } | null>(null);

  useEffect(() => {
    if (open) {
      // Reset on open so re-opens don't carry over stale state.
      setTitle("");
      setDescription("");
      setPriority("Medium");
      setDueDate("");
      setAssigneeId(null);
      setTagIds([]);
      setTitleError(null);
      setIsMultiuser(false);
      setParticipants([]);
      setParticipantPickerKey(0);
      setPickedAttachment(null);
      setPickerOpen(false);
    }
  }, [open]);

  // Prefilled values take precedence over picker selection. When prefilled
  // is set the picker UI is hidden entirely (the chip below shows the
  // locked record).
  const effectiveRelatedType = prefilledRelatedType ?? pickedAttachment?.type;
  const effectiveRelatedId = prefilledRelatedEntityId ?? pickedAttachment?.id;

  // The owner (assignee) defaults to the creator and is never also a
  // participant.
  const ownerId = assigneeId ?? user?.id ?? null;
  const addParticipant = (id: string | null, displayName: string) => {
    // Remount the add-picker so it clears after a selection.
    setParticipantPickerKey((k) => k + 1);
    if (!id || id === ownerId) return;
    setParticipants((prev) =>
      prev.some((p) => p.id === id) ? prev : [...prev, { id, displayName }],
    );
  };

  const submit = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setTitleError("Title is required.");
      return;
    }
    if (isMultiuser && participants.length === 0) {
      toast({ message: "Add at least one participant for a multiuser task.", tone: "error" });
      return;
    }
    setTitleError(null);
    const payload: CreateWorkTaskDto = {
      title: trimmed,
      description: description.trim() || undefined,
      priority,
      dueDate: calendarDateInputToIso(dueDate) ?? undefined,
      assigneeId: assigneeId ?? undefined,
      tagIds: tagIds.length ? tagIds : undefined,
      relatedEntityType: effectiveRelatedType,
      relatedEntityId: effectiveRelatedId,
      isMultiuser: isMultiuser || undefined,
      participantIds: isMultiuser ? participants.map((p) => p.id) : undefined,
    };
    try {
      const created = await create.mutateAsync(payload);
      toast({ message: "Task created.", tone: "success" });
      onCreated?.(created.id);
      onClose();
      if (navigateOnSave) {
        navigate(`/tasks/${created.id}`);
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.errors?.[0] ?? err.message
          : "Could not create task.";
      toast({ message: msg, tone: "error" });
    }
  };

  const tags = tagsQ.data ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="New task"
      description="Lightweight todo or team coordination item."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={submit} loading={create.isPending} type="button">
            Save task
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Title" required error={titleError ?? undefined} htmlFor="qa-title">
          <Input
            id="qa-title"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to happen?"
            invalid={Boolean(titleError)}
          />
        </FormField>

        <FormField label="Description" htmlFor="qa-desc">
          <Textarea
            id="qa-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional context, links, acceptance criteria."
          />
        </FormField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <FormField label="Priority" htmlFor="qa-priority">
            <Select
              id="qa-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as WorkTaskPriority)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Due date" htmlFor="qa-due">
            <Input
              id="qa-due"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </FormField>
        </div>

        <FormField
          label="Assignee"
          htmlFor="qa-assignee"
          helperText={
            canAssignToOthers
              ? "Leave blank to assign to yourself."
              : "Tasks you create are assigned to you. Ask an admin for cross-user assignment."
          }
        >
          {canAssignToOthers ? (
            <UserPicker
              value={assigneeId}
              ariaLabel="Assignee"
              placeholder={user ? `${user.displayName} (you)` : "Search users"}
              allowUnassigned
              onChange={(id) => setAssigneeId(id)}
            />
          ) : (
            <Input
              id="qa-assignee"
              readOnly
              value={user?.displayName ?? ""}
              disabled
              aria-label="Assignee"
            />
          )}
        </FormField>

        <FormField
          label="Multiuser task"
          htmlFor="qa-multiuser"
          helperText="Share this task with others. Participants can comment, change status, and move the progress slider."
        >
          <div className="flex items-center gap-space-2 text-small text-onyx">
            <input
              id="qa-multiuser"
              type="checkbox"
              checked={isMultiuser}
              onChange={(e) => setIsMultiuser(e.target.checked)}
              className="accent-gold"
            />
            <span>Share with participants</span>
          </div>
        </FormField>

        {isMultiuser && (
          <FormField
            label="Participants"
            htmlFor="qa-participants"
            helperText="Add one or more people. A multiuser task needs at least one participant."
          >
            <div className="flex flex-col gap-space-2">
              <UserPicker
                key={participantPickerKey}
                value={null}
                ariaLabel="Add participant"
                placeholder="Search users to add"
                onChange={addParticipant}
              />
              {participants.length > 0 && (
                <div className="flex flex-wrap gap-space-2">
                  {participants.map((p) => (
                    <span
                      key={p.id}
                      className="inline-flex items-center gap-space-1 rounded-sm bg-white-warm border border-border-light px-space-2 py-[3px] text-small text-onyx"
                    >
                      {p.displayName}
                      <button
                        type="button"
                        onClick={() =>
                          setParticipants((prev) => prev.filter((x) => x.id !== p.id))
                        }
                        aria-label={`Remove ${p.displayName}`}
                        className="text-gray-mid hover:text-onyx"
                      >
                        <X size={12} aria-hidden />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </FormField>
        )}

        {tags.length > 0 && (
          <FormField label="Tags" htmlFor="qa-tags" helperText="Click to toggle.">
            <div id="qa-tags" className="flex flex-wrap gap-space-2">
              {tags.map((t) => {
                const active = tagIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() =>
                      setTagIds((prev) =>
                        active ? prev.filter((x) => x !== t.id) : [...prev, t.id],
                      )
                    }
                    className={
                      "px-space-2 py-[3px] rounded-sm font-display text-[10px] " +
                      "font-bold uppercase tracking-[0.12em] border " +
                      (active
                        ? "bg-gold text-onyx border-gold"
                        : "bg-white-pure text-gray-mid border-border-light hover:bg-white-warm")
                    }
                  >
                    {t.displayName}
                  </button>
                );
              })}
            </div>
          </FormField>
        )}

        {prefilledRelatedEntityLabel ? (
          <div className="rounded-md bg-white-warm border border-border-light px-space-3 py-space-2 text-small text-onyx">
            Linked to {prefilledRelatedEntityLabel}.
          </div>
        ) : (
          <FormField
            label="Attach to a record"
            htmlFor="qa-attach"
            helperText="Optional. Link this task to any record in the system."
          >
            {pickedAttachment ? (
              <div className="flex items-center justify-between gap-space-3 rounded-md bg-white-warm border border-border-light px-space-3 py-space-2 text-small text-onyx">
                <span className="truncate">{pickedAttachment.label}</span>
                <button
                  type="button"
                  onClick={() => setPickedAttachment(null)}
                  aria-label="Remove attached record"
                  className="shrink-0 text-gray-mid hover:text-onyx"
                >
                  <X size={14} aria-hidden />
                </button>
              </div>
            ) : (
              <Button
                id="qa-attach"
                type="button"
                variant="secondary"
                onClick={() => setPickerOpen(true)}
              >
                + Attach a record
              </Button>
            )}
          </FormField>
        )}
      </div>

      {!prefilledRelatedType && (
        <AttachResourceModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={(attachment) => {
            const taskType = taskTypeForMessageType(attachment.resourceType);
            if (!taskType) return;
            setPickedAttachment({
              type: taskType,
              id: attachment.resourceId,
              label: attachment.displayLabel,
            });
          }}
        />
      )}
    </Modal>
  );
}
